import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, ProgressBar, Button, Alert, Modal, Spinner } from "react-bootstrap";
import { FaTrash, FaDatabase, FaInfoCircle, FaHdd, FaFileArchive } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import { Navigate, useLocation } from "react-router-dom";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import "react-toastify/dist/ReactToastify.css";

const StorageManagement = () => {
  const queryParams = new URLSearchParams(useLocation().search);
  const isEmbedded = window.self !== window.top || queryParams.get("token");

  const [userInfo] = useState(() => {
    // 1. Check URL query parameters first
    const tokenParam = queryParams.get("token");
    if (tokenParam) {
      sessionStorage.setItem("token", tokenParam);
      try {
        return jwt_decode(tokenParam);
      } catch (e) {
        console.error("Invalid URL token", e);
      }
    }
    // 2. Fall back to sessionStorage token
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        return jwt_decode(token);
      } catch (e) {
        console.error("Invalid session token", e);
      }
    }
    return {};
  });

  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [storageData, setStorageData] = useState(null);
  const [cleanupDetails, setCleanupDetails] = useState({ count: 0, size_bytes: 0 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Role Protection Check
  const hasAccess = userInfo.userrole === "ADMIN" || userInfo.userrole === "SYS_ADMIN";

  const fetchStorageData = async () => {
    try {
      setLoading(true);
      const [storageRes, cleanupRes] = await Promise.all([
        WhatsAppAPI.getCompanyStorage(),
        WhatsAppAPI.getHistoryFilesCleanupDetails()
      ]);

      if (storageRes && storageRes.success) {
        setStorageData(storageRes.storage);
      }
      if (cleanupRes && cleanupRes.success) {
        setCleanupDetails(cleanupRes.details);
      }
    } catch (error) {
      console.error("Error fetching storage stats:", error);
      toast.error("Failed to load storage statistics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorageData();
  }, []);

  const handleCleanup = async () => {
    try {
      setCleaning(true);
      setShowConfirmModal(false);
      const res = await WhatsAppAPI.cleanupHistoryFiles();
      if (res && res.success) {
        toast.success(res.message || "Chat history files cleaned up successfully!");
        // Refresh details after deletion
        await fetchStorageData();
      } else {
        toast.error(res.message || "Failed to perform storage cleanup.");
      }
    } catch (error) {
      console.error("Error cleaning up storage:", error);
      toast.error("An error occurred during cleanup.");
    } finally {
      setCleaning(false);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0 || !bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Calculations
  const dbBytes = Number(storageData?.db_size_bytes) || 0;
  const fileBytes = Number(storageData?.file_size_bytes) || 0;
  const totalUsedBytes = dbBytes + fileBytes;

  const baseGB = Number(storageData?.base_storage_gb) || 5;
  const addonGB = Number(storageData?.addon_gb) || 0;
  const totalAllowedBytes = (baseGB + addonGB) * 1024 * 1024 * 1024;

  const usedPercentage = Math.min(100, Math.max(0.5, ((totalUsedBytes / totalAllowedBytes) * 100)));
  const dbPercentage = (dbBytes / totalAllowedBytes) * 100;
  const filePercentage = (fileBytes / totalAllowedBytes) * 100;

  let progressBarVariant = "success";
  if (usedPercentage > 85) progressBarVariant = "danger";
  else if (usedPercentage > 65) progressBarVariant = "warning";

  if (!hasAccess) {
    return <Navigate to="/403" replace />;
  }

  return (
    <>
      <Container className={isEmbedded ? "py-2 px-3" : "mt-5"}>
        {/* Title & Quick Guide */}
        <Row className={isEmbedded ? "mb-4" : "mx-5 mb-4"}>
          <Col>
            <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
              <h5 className="mb-2 fw-bold d-flex align-items-center text-dark">
                <FaDatabase className="me-2 text-success" /> Storage Management & Cleanup
              </h5>
              <small className="text-secondary">
                Monitor your workspace storage and free up space by cleaning up old chat attachments.
              </small>
            </Card>
          </Col>
        </Row>

      {loading ? (
        <div className="text-center my-5 py-5">
          <Spinner animation="border" variant="success" style={{ width: "3rem", height: "3rem" }} />
          <p className="mt-3 text-secondary">Loading storage insights...</p>
        </div>
      ) : (
        <Row className={isEmbedded ? "g-4" : "mx-5 g-4"}>
          {/* Left Column: Storage Usage and Progress */}
          <Col lg={7} md={12}>
            <Card className="shadow-sm border-0 rounded-3 h-100">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4 text-dark d-flex align-items-center">
                  <FaHdd className="me-2 text-success" /> Disk Usage Summary
                </h5>

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold text-secondary">Total Used Storage</span>
                    <span className="fw-bold text-dark">
                      {formatBytes(totalUsedBytes)} / {baseGB + addonGB} GB
                    </span>
                  </div>
                  <ProgressBar now={usedPercentage} variant={progressBarVariant} className="rounded-pill" style={{ height: "12px" }} />
                  <div className="d-flex justify-content-between mt-1 text-muted" style={{ fontSize: "0.85rem" }}>
                    <span>{usedPercentage.toFixed(1)}% utilized</span>
                    <span>{formatBytes(totalAllowedBytes - totalUsedBytes)} remaining</span>
                  </div>
                </div>

                <hr className="my-4 text-black-50" />

                {/* Storage breakdown */}
                <Row className="g-3">
                  <Col sm={6}>
                    <div className="p-3 border rounded-3 bg-light d-flex align-items-center">
                      <div className="bg-primary text-white p-2 rounded-3 me-3">
                        <FaDatabase size={20} />
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: "0.85rem" }}>Database Size</div>
                        <h6 className="fw-bold mb-0 text-dark">{formatBytes(dbBytes)}</h6>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="p-3 border rounded-3 bg-light d-flex align-items-center">
                      <div className="bg-info text-white p-2 rounded-3 me-3">
                        <FaFileArchive size={20} />
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: "0.85rem" }}>File Uploads Size</div>
                        <h6 className="fw-bold mb-0 text-dark">{formatBytes(fileBytes)}</h6>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column: Chat History File Cleanup */}
          <Col lg={5} md={12}>
            <Card className="shadow-sm border-0 rounded-3 h-100">
              <Card.Body className="p-4 d-flex flex-column justify-content-between">
                <div>
                  <h5 className="fw-bold mb-4 text-dark d-flex align-items-center">
                    <FaTrash className="me-2 text-danger" /> Chat Attachment Cleanup
                  </h5>

                  <div className="p-4 border border-danger border-opacity-25 rounded-3 bg-danger bg-opacity-10 text-center mb-4">
                    <div className="text-danger fw-bold fs-4 mb-1">
                      {cleanupDetails.count} Files
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Occupy a total of
                    </div>
                    <div className="text-dark fw-bold fs-3 mt-1">
                      {formatBytes(cleanupDetails.size_bytes)}
                    </div>
                  </div>

                  <Alert variant="warning" className="border-0 shadow-sm mb-4">
                    <div className="d-flex">
                      <FaInfoCircle className="mt-1 me-2 flex-shrink-0" size={18} />
                      <div style={{ fontSize: "0.88rem" }}>
                        <strong>What will be deleted?</strong> This cleans up image, video, and document attachments from your chat/messenger history.
                        <br />
                        <span className="fw-semibold">No message texts, active campaigns, interactive files, or chatbot flows will be deleted.</span>
                      </div>
                    </div>
                  </Alert>
                </div>

                <Button
                  variant="danger"
                  className="w-100 py-2 fs-6 fw-semibold shadow-sm d-flex align-items-center justify-content-center"
                  disabled={cleanupDetails.count === 0 || cleaning}
                  onClick={() => setShowConfirmModal(true)}
                >
                  {cleaning ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Cleaning Storage...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" /> Clean Chat History Files
                    </>
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-danger">Confirm Storage Cleanup</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          <p>
            You are about to permanently delete <strong className="text-dark">{cleanupDetails.count} files</strong> (totaling <strong>{formatBytes(cleanupDetails.size_bytes)}</strong>) from your chat history.
          </p>
          <p className="text-danger fw-semibold mb-0" style={{ fontSize: "0.9rem" }}>
            ⚠️ This action is irreversible. All physical uploads associated with these records will be deleted. Chat log text histories will be preserved.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={cleaning}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleCleanup} disabled={cleaning}>
            Yes, Delete Attachments
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={5000} />
    </Container>
  </>
  );
};

export default StorageManagement;
